/**
 * Searchbar functionality
 * Handles search input, suggestions, and predictive search
 */

// Utility functions
function safeQuerySelector(selector, context = document) {
  try {
    return context.querySelector(selector);
  } catch (error) {
    return null;
  }
}

function safeQuerySelectorAll(selector, context = document) {
  try {
    return Array.from(context.querySelectorAll(selector));
  } catch (error) {
    return [];
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function formatPrice(price) {
  const currency = window.Shopify?.currency?.active || 'USD';
  // Check if price is already in dollars (less than 100) or in cents (100 or more)
  const formattedPrice = price < 100 ? price : price / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(formattedPrice);
}

class Searchbar {
  constructor() {
    this.searchInput = safeQuerySelector('#search-input');
    this.searchSuggestions = safeQuerySelector('#search-suggestions');
    this.searchResults = safeQuerySelector('#search-results');
    this.loadingElement = safeQuerySelector('.search-suggestions__loading');
    this.predictiveContainer = safeQuerySelector('#predictive-search-container');
    this.viewAllButton = safeQuerySelector('#view-all-results');
    
    this.searchTimeout = null;
    this.isSearching = false;
    
    this.init();
  }
  
  init() {
    if (!this.searchInput) return;
    
    this.setupEventListeners();
    this.setupViewAllButton();
  }
  
  setupEventListeners() {
    // Input event with debouncing
    this.searchInput.addEventListener('input', debounce((e) => {
      const query = e.target.value.trim();
      if (query.length >= 2) {
        this.performSearch(query);
      } else {
        this.hideSuggestions();
      }
    }, 300));
    
    // Focus event
    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.trim().length >= 2) {
        this.showSuggestions();
      }
    });
    
    // Blur event with delay to allow clicking on suggestions
    this.searchInput.addEventListener('blur', () => {
      setTimeout(() => this.hideSuggestions(), 200);
    });
    
    // Keyboard navigation
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideSuggestions();
        this.searchInput.blur();
      }
    });
  }
  
  setupViewAllButton() {
    if (!this.viewAllButton) return;
    
    this.viewAllButton.addEventListener('click', () => {
      const query = this.searchInput.value.trim();
      if (query) {
        window.location.href = `${window.Shopify?.routes?.search_url || '/search'}?q=${encodeURIComponent(query)}`;
      }
    });
  }
  
  async performSearch(query) {
    if (this.isSearching) return;
    
    this.isSearching = true;
    this.showSuggestions();
    this.showLoading();
    
    try {
      // Try JSON API first for fast results
      const success = await this.performJsonSearch(query);
      if (!success) {
        // Fallback to section-based search
        await this.performSectionSearch(query);
      }
    } catch (error) {
      console.error('Search error:', error);
      this.showNoResults();
    } finally {
      this.hideLoading();
      this.isSearching = false;
    }
  }
  
  async performJsonSearch(query) {
    try {
      // Try the standard Shopify search suggest API format
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Try different possible data structures
      let products = [];
      
      // Check various possible paths in the response
      if (data.resources?.results?.products) {
        products = data.resources.results.products;
      } else if (data.products) {
        products = data.products;
      } else if (data.results?.products) {
        products = data.results.products;
      } else if (data.suggestions?.products) {
        products = data.suggestions.products;
      } else if (data.resources?.products) {
        products = data.resources.products;
      } else if (Array.isArray(data)) {
        products = data;
      }
      
      
      if (products.length === 0) {
        return false;
      }
      
      this.displayJsonResults(products);
      return true;
      
    } catch (error) {
      console.error('JSON search error:', error);
      return false;
    }
  }
  
  displayJsonResults(products) {
    if (!this.searchResults) return;
    
    
    // Limit to 5 results
    const limitedProducts = products.slice(0, 5);
    
    const suggestionsHTML = limitedProducts.map(product => {
      const title = product.title || '';
      const url = product.url || '';
      const rawPrice = product.price;
      const price = rawPrice ? formatPrice(rawPrice) : '';
      const imageSrc = product.featured_image?.url || product.image || '';
      const imageAlt = product.featured_image?.alt || title;
      const vendor = product.vendor || '';
      const available = product.available;
      
      // Debug logging
      
      return `
        <a href="${url}" class="search-suggestion">
          ${imageSrc ? `<img src="${imageSrc}" alt="${imageAlt}" class="search-suggestion__image" loading="lazy">` : '<div class="search-suggestion__image"></div>'}
          <div class="search-suggestion__content">
            <div class="search-suggestion__title">${title}</div>
            ${vendor ? `<div class="search-suggestion__vendor">${vendor}</div>` : ''}
            ${!available ? '<div class="search-suggestion__availability">Sold Out</div>' : ''}
          </div>
          ${price ? `<div class="search-suggestion__price">${price}</div>` : ''}
        </a>
      `;
    }).join('');
    
    this.searchResults.innerHTML = suggestionsHTML;
    this.showSuggestions();
  }
  
  async performSectionSearch(query) {
    if (!this.predictiveContainer || !this.searchSuggestions) return;
    
    try {
      const searchUrl = `${window.Shopify?.routes?.root || ''}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=5&section_id=predictive-search`;
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const sectionContent = doc.querySelector('#shopify-section-predictive-search');
      
      if (sectionContent && sectionContent.innerHTML.trim()) {
        this.searchSuggestions.style.display = 'none';
        this.predictiveContainer.innerHTML = sectionContent.innerHTML;
        this.predictiveContainer.style.display = 'block';
        this.showSuggestions();
        return;
      }
      
      // Fallback to simple search
      this.performSimpleSearch(query);
      
    } catch (error) {
      console.error('Section search error:', error);
      this.performSimpleSearch(query);
    }
  }
  
  performSimpleSearch(query) {
    if (!this.searchResults) return;
    
    
    // Show regular suggestions and hide predictive search
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'block';
    if (this.predictiveContainer) this.predictiveContainer.style.display = 'none';
    
    const allProducts = safeQuerySelectorAll('[data-product-id], .product-card, .product-item');
    
    if (allProducts.length === 0) {
      this.showNoResults();
      return;
    }
    
    let suggestionsHTML = '';
    let count = 0;
    
    allProducts.forEach(product => {
      if (count >= 5) return;
      
      const titleElement = product.querySelector('h1, h2, h3, h4, h5, h6, .title, .product-title, a');
      const linkElement = product.querySelector('a');
      const imageElement = product.querySelector('img');
      const priceElement = product.querySelector('.price, .product-price, [class*="price"]');
      
      if (titleElement && linkElement) {
        const title = titleElement.textContent.trim();
        const url = linkElement.href;
        
        // Simple text matching
        if (title.toLowerCase().includes(query.toLowerCase())) {
          const imageSrc = imageElement ? imageElement.src : '';
          const imageAlt = imageElement ? imageElement.alt : title;
          const price = priceElement ? priceElement.textContent.trim() : '';
          
          suggestionsHTML += `
            <a href="${url}" class="search-suggestion">
              ${imageSrc ? `<img src="${imageSrc}" alt="${imageAlt}" class="search-suggestion__image" loading="lazy">` : '<div class="search-suggestion__image"></div>'}
              <div class="search-suggestion__content">
                <div class="search-suggestion__title">${title}</div>
              </div>
              ${price ? `<div class="search-suggestion__price">${price}</div>` : ''}
            </a>
          `;
          count++;
        }
      }
    });
    
    
    if (suggestionsHTML) {
      this.searchResults.innerHTML = suggestionsHTML;
    } else {
      this.showNoResults();
    }
  }
  
  showSuggestions() {
    if (this.searchSuggestions) {
      this.searchSuggestions.classList.add('active');
    }
  }
  
  hideSuggestions() {
    if (this.searchSuggestions) {
      this.searchSuggestions.classList.remove('active');
    }
    if (this.predictiveContainer) {
      this.predictiveContainer.style.display = 'none';
    }
  }
  
  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.style.display = 'flex';
    }
    if (this.searchResults) {
      this.searchResults.innerHTML = '';
    }
  }
  
  hideLoading() {
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
  }
  
  showNoResults() {
    if (this.searchResults) {
      this.searchResults.innerHTML = `
        <div class="search-suggestions__no-results">
          No products found
        </div>
      `;
    }
  }
}

// Initialize searchbar when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new Searchbar();
});
